import { useUploader } from "@components/public/Uploader";
import CloudUploadOutlined from "@mui/icons-material/CloudUploadOutlined";
import { Box, IconButton, styled, Typography } from "@mui/material";
import CircularProgress, {
  CircularProgressProps,
} from "@mui/material/CircularProgress";
import * as React from "react";

const Input = styled("input")({
  display: "none",
});

const path = "images/review/temp/";

const MAX_FILE_SIZE = 10485760;

function CircularProgressWithLabel(
  props: CircularProgressProps & { value: number }
) {
  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <CircularProgress {...props} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="caption"
          component="div"
          color="text.secondary"
        >{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  );
}

export default function ImageUploader({
  onFinish,
  disabled,
}: {
  onFinish: (data) => void;
  disabled: boolean;
}): React.ReactElement {
  const [loading, setLoading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const uploadInputRef = React.useRef(null);

  const onUploadStart = (file, next) => {
    if (file.size > MAX_FILE_SIZE) {
      return false;
    }
    setLoading(true);
    next(file);
  };

  const onProgress = (progress) => {
    setProgress(progress);
  };

  const onError = () => {
    setLoading(false);
    setProgress(0);
  };

  const handleFinish = (data) => {
    onFinish(data);
    setProgress(100);
    setLoading(false);
    setProgress(0);
  };

  useUploader(
    { path, onUploadStart, onProgress, onError, onFinish: handleFinish },
    uploadInputRef
  );

  return (
    <label htmlFor="review-image-uploader">
      <Input
        accept="image/*"
        id="review-image-uploader"
        type="file"
        disabled={loading}
        ref={uploadInputRef}
      />
      {loading && <CircularProgressWithLabel value={progress} />}
      {!loading && (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            color="primary"
            aria-label="upload review image"
            component="span"
            disabled={disabled || loading}
          >
            <CloudUploadOutlined />
          </IconButton>
          <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
            Maximum file size 10 MB
          </Typography>
        </Box>
      )}
    </label>
  );
}
